import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Row, Col, Card, Button, Form } from 'react-bootstrap';
import { companyService } from '../services/apiService';
import { useAuthToken } from '../hooks/useAuthToken';
import { useIsMobile } from '../hooks/useMediaQuery';
import SegmentedControl from '../components/SegmentedControl';
import ReportHeader from '../components/ReportHeader';
import { LoadingState, ErrorState, serverMessage } from '../components/PageState';
import { useChartTheme } from '../components/chartTheme';
import { formatDate, formatShortDate, formatMonthYear, toLocalIsoDate } from '../utils/format';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';

const compactNumber = new Intl.NumberFormat('en-GB', { notation: 'compact', maximumSignificantDigits: 3 });

const PRESETS = [
  { label: '3m', months: 3 },
  { label: '6m', months: 6 },
  { label: '12m', months: 12 },
  { label: '3y', months: 36 },
  { label: '5y', months: 60 },
];

// Week keys are local calendar dates ("2026-08-03"), read as local midnight so they never shift a day
const weekDate = (weekStart) => new Date(`${weekStart}T00:00:00`);

// From the given number of months ago up to today, as local calendar dates
const presetRange = (months) => {
  const now = new Date();
  return {
    from: toLocalIsoDate(new Date(now.getFullYear(), now.getMonth() - months, now.getDate())),
    to: toLocalIsoDate(now),
  };
};

const rangeProblem = ({ from, to }) => {
  if (!from || !to) return 'Enter both dates.';
  if (from > to) return 'From must be on or before To.';
  return null;
};

const SystemReport = () => {
  const ensureToken = useAuthToken();
  const isMobile = useIsMobile();
  const chartTheme = useChartTheme();
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // The window the chart shows, and the dates being edited; edits apply with the Apply button
  const [range, setRange] = useState(() => presetRange(12));
  const [draft, setDraft] = useState(range);
  const [activePreset, setActivePreset] = useState('12m');
  // Only the newest request may update the chart
  const requestId = useRef(0);

  const draftProblem = rangeProblem(draft);
  const draftChanged = draft.from !== range.from || draft.to !== range.to;

  const applyPreset = (label) => {
    const next = presetRange(PRESETS.find((p) => p.label === label).months);
    setRange(next);
    setDraft(next);
    setActivePreset(label);
  };

  const applyDraft = (e) => {
    e.preventDefault();
    if (draftProblem || !draftChanged) return;
    setRange(draft);
    setActivePreset(null);
  };

  const fetchData = useCallback(async () => {
    const id = ++requestId.current;
    try {
      setLoading(true);
      setError(null);
      await ensureToken();
      const response = await companyService.getWeeklyWhereaboutsByType(range.from, range.to);
      if (id !== requestId.current) return;
      setChartData(response.data);
    } catch (err) {
      if (id !== requestId.current) return;
      console.error('Failed to load whereabouts data:', err);
      setError(serverMessage(err, 'Could not load whereabouts by type.'));
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [ensureToken, range]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Short windows label weeks by day ("3 Aug"), longer ones by month ("Aug 26")
  const spanDays = (new Date(range.to) - new Date(range.from)) / (24 * 60 * 60 * 1000);
  const tickFormatter = (weekStart) => (spanDays <= 190 && !isMobile
    ? formatShortDate(weekDate(weekStart))
    : formatMonthYear(weekDate(weekStart)));

  const renderChart = () => {
    if (error) return <ErrorState message={error} onRetry={fetchData} inCard />;
    if (loading || !chartData) return <LoadingState label="Loading whereabouts" />;
    if (chartData.length === 0) return <div className="p-4 text-center text-muted">No whereabouts in this window</div>;
    return (
      <ResponsiveContainer width="100%" height={isMobile ? 280 : 400}>
        <LineChart data={chartData} margin={isMobile ? { top: 5, right: 8, left: 0, bottom: 5 } : { top: 5, right: 30, left: 20, bottom: 5 }}>
          <CartesianGrid {...chartTheme.grid} />
          <XAxis dataKey="weekStart" {...chartTheme.xAxis} tickFormatter={tickFormatter} minTickGap={16} />
          <YAxis
            {...chartTheme.yAxis}
            tickFormatter={(v) => compactNumber.format(v)}
            {...(isMobile ? { width: 40 } : {})}
          />
          <Tooltip
            {...chartTheme.tooltip}
            labelFormatter={(weekStart) => `Week of ${formatDate(weekDate(weekStart))}`}
            formatter={(value, name) => [Number(value).toLocaleString('en-GB'), name]}
          />
          <Legend {...chartTheme.legend} />
          <Line type="monotone" dataKey="Home" stroke={chartTheme.colors.ordinal[0]} strokeWidth={2} dot={isMobile ? false : { r: 3 }} />
          <Line type="monotone" dataKey="Office" stroke={chartTheme.colors.ordinal[1]} strokeWidth={2} dot={isMobile ? false : { r: 3 }} />
          <Line type="monotone" dataKey="Other" stroke={chartTheme.colors.ordinal[2]} strokeWidth={2} dot={isMobile ? false : { r: 3 }} />
        </LineChart>
      </ResponsiveContainer>
    );
  };

  return (
    <Container fluid className="py-4">
      <ReportHeader
        title="Whereabouts by type"
        description="Days people set as home, office or other, per week, across every company"
      />

      <Row>
        <Col>
          <Card className="shadow-sm">
            {/* The controls wrap below the title, and onto several lines, when the header runs out of room */}
            <Card.Header className="bg-light d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h5 className="mb-0">Days per week</h5>
              <div className="d-flex align-items-center flex-wrap gap-3">
                <SegmentedControl
                  options={PRESETS.map((p) => ({ value: p.label, label: p.label }))}
                  value={activePreset}
                  onChange={applyPreset}
                  ariaLabel="Time window"
                />
                <Form onSubmit={applyDraft} className="d-flex align-items-center flex-wrap gap-2" noValidate>
                  <Form.Group controlId="system-report-from" className="d-flex align-items-center gap-2">
                    <Form.Label className="mb-0" style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>From</Form.Label>
                    <Form.Control
                      type="date"
                      value={draft.from}
                      max={draft.to || undefined}
                      onChange={(e) => setDraft((d) => ({ ...d, from: e.target.value }))}
                      isInvalid={!!draftProblem}
                      size="sm"
                      style={{ width: '160px' }}
                    />
                  </Form.Group>
                  <Form.Group controlId="system-report-to" className="d-flex align-items-center gap-2">
                    <Form.Label className="mb-0" style={{ fontSize: '13px', whiteSpace: 'nowrap' }}>To</Form.Label>
                    <Form.Control
                      type="date"
                      value={draft.to}
                      min={draft.from || undefined}
                      onChange={(e) => setDraft((d) => ({ ...d, to: e.target.value }))}
                      isInvalid={!!draftProblem}
                      size="sm"
                      style={{ width: '160px' }}
                    />
                  </Form.Group>
                  <Button type="submit" size="sm" variant="outline-secondary" disabled={!!draftProblem || !draftChanged}>
                    Apply
                  </Button>
                  {draftProblem && <span className="small text-danger" role="alert">{draftProblem}</span>}
                </Form>
              </div>
            </Card.Header>
            <Card.Body>
              {renderChart()}
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  );
};

export default SystemReport;
